import React, { useState } from 'react';
import { BottomSheet } from './BottomSheet';
import { personOutsideCast } from '../../utils/narrativePov';
import {
  ADD_PEOPLE_IN_NOTES,
  ADD_POV_PERSON,
  ADD_POV_PERSON_BUTTON,
  ADD_POV_PERSON_FAILED,
  POV_OUTSIDE_CAST,
  WHO_WE_ARE_WITH,
  WHOSE_HEAD,
  WHOSE_HEAD_ALL,
  WHOSE_HEAD_GO_BACK,
  WHOSE_HEAD_LOADING,
  WHOSE_HEAD_NOT_SET,
  WHOSE_HEAD_WARN,
} from '../../utils/writerCopy';
import {
  WHOSE_HEAD_CHOICE_ALL,
  WHOSE_HEAD_CHOICE_NOT_SET,
  WHOSE_HEAD_CHOICE_PERSON,
} from '../../utils/plottrackGrid';
import './WhoseHeadControl.css';

function optionKey(value) {
  if (!value || value.choice === WHOSE_HEAD_CHOICE_NOT_SET) return WHOSE_HEAD_CHOICE_NOT_SET;
  if (value.choice === WHOSE_HEAD_CHOICE_ALL) return WHOSE_HEAD_CHOICE_ALL;
  if (value.choice === WHOSE_HEAD_CHOICE_PERSON && value.entityId) {
    return `person:${value.entityId}`;
  }
  return WHOSE_HEAD_CHOICE_NOT_SET;
}

export function whoseHeadLabel(value, people = []) {
  if (!value || value.choice === WHOSE_HEAD_CHOICE_NOT_SET) return WHOSE_HEAD_NOT_SET;
  if (value.choice === WHOSE_HEAD_CHOICE_ALL) return WHOSE_HEAD_ALL;
  const named = (value.entityName || '').trim();
  if (named) return named;
  const match = (people || []).find((person) => String(person.id) === String(value.entityId));
  return (match?.name || '').trim() || WHOSE_HEAD_NOT_SET;
}

function parseKey(key, people) {
  if (key === WHOSE_HEAD_CHOICE_ALL) {
    return { choice: WHOSE_HEAD_CHOICE_ALL, entityId: '', entityName: '' };
  }
  if (key.startsWith('person:')) {
    const entityId = key.slice(7);
    const match = (people || []).find((person) => String(person.id) === String(entityId));
    return {
      choice: WHOSE_HEAD_CHOICE_PERSON,
      entityId,
      entityName: (match?.name || '').trim(),
    };
  }
  return { choice: WHOSE_HEAD_CHOICE_NOT_SET, entityId: '', entityName: '' };
}

/**
 * Shared Whose head picker (#1133). Not set · All · people names.
 * Wide: native select. Narrow: high-contrast chip → same options in a sheet.
 * Head is not a voice card.
 */
function peopleForCast(people, castIds) {
  if (!castIds || !castIds.length) {
    return (people || []).map((person) => ({ ...person, outside: false }));
  }
  const cast = new Set(castIds.map(String));
  const inside = [];
  const outside = [];
  (people || []).forEach((person) => {
    if (cast.has(String(person.id))) inside.push({ ...person, outside: false });
    else outside.push({ ...person, outside: true });
  });
  return [...inside, ...outside];
}

// Run 43: no people yet means no POV choice. Type the name here; it becomes
// a character note in this book and this scene's POV character.
function AddPersonForm({ id, disabled, onAddPerson }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const trimmed = name.trim();
  const submit = async (e) => {
    e.preventDefault();
    if (!trimmed || busy || disabled) return;
    setBusy(true);
    setFailed(false);
    try {
      await onAddPerson(trimmed);
      setName('');
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="whose-head-add" onSubmit={submit} data-testid="whose-head-add">
      <label htmlFor={`${id}-add`} className="whose-head-picker__hint">{ADD_POV_PERSON}</label>
      <div className="whose-head-add__row">
        <input
          id={`${id}-add`}
          type="text"
          value={name}
          disabled={disabled || busy}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" disabled={disabled || busy || !trimmed}>
          {ADD_POV_PERSON_BUTTON}
        </button>
      </div>
      {failed ? <p className="whose-head-picker__hint" role="alert">{ADD_POV_PERSON_FAILED}</p> : null}
    </form>
  );
}

export default function WhoseHeadControl({
  value,
  people = [],
  onChange,
  variant = 'select',
  id = 'whose-head',
  disabled = false,
  castIds = null,
  emptyAction = null,
  onAddPerson = null,
  loading = false,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  if (loading) {
    // Run 51: the previous scene's person showed until this scene's POV arrived.
    if (variant !== 'chip') {
      return (
        <div className="whose-head-picker" data-testid="whose-head-picker" aria-busy="true">
          <label className="whose-head-picker__field" htmlFor={id}>
            <span>{WHOSE_HEAD}</span>
            <select id={id} value="loading" disabled onChange={() => {}}>
              <option value="loading">{WHOSE_HEAD_LOADING}</option>
            </select>
          </label>
        </div>
      );
    }
    return (
      <div className="whose-head" aria-busy="true">
        <button
          type="button"
          className="whose-head-chip"
          aria-label={`${WHO_WE_ARE_WITH}: ${WHOSE_HEAD_LOADING}`}
          data-testid="whose-head-chip"
          disabled
        >
          <span className="whose-head-chip__primary">{WHO_WE_ARE_WITH}</span>
          <span className="whose-head-chip__secondary">{WHOSE_HEAD_LOADING}</span>
        </button>
      </div>
    );
  }
  const ordered = peopleForCast(people, castIds);
  const currentKey = optionKey(value);
  const currentLabel = whoseHeadLabel(value, people);
  const outside = personOutsideCast(value, castIds);
  const options = [
    { key: WHOSE_HEAD_CHOICE_NOT_SET, label: WHOSE_HEAD_NOT_SET },
    { key: WHOSE_HEAD_CHOICE_ALL, label: WHOSE_HEAD_ALL },
    ...ordered.map((person) => ({
      key: `person:${person.id}`,
      label: person.outside ? `${person.name} (not a POV character)` : person.name,
    })),
  ];

  const pick = (key) => {
    if (disabled) return;
    onChange?.(parseKey(key, people));
    setSheetOpen(false);
  };

  const picker = (
    <div className="whose-head-picker" data-testid="whose-head-picker">
      <label className="whose-head-picker__field" htmlFor={id}>
        <span>{WHOSE_HEAD}</span>
        <select
          id={id}
          value={currentKey}
          disabled={disabled}
          onChange={(e) => pick(e.target.value)}
        >
          {options.map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>
      </label>
      {people.length === 0 && !onAddPerson && (
        <p className="whose-head-picker__hint">{ADD_PEOPLE_IN_NOTES}</p>
      )}
      {people.length === 0 && onAddPerson && (
        <AddPersonForm id={id} disabled={disabled} onAddPerson={onAddPerson} />
      )}
      {people.length === 0 && emptyAction}
      {outside ? (
        <p className="whose-head-picker__hint" data-testid="whose-head-outside-cast">{POV_OUTSIDE_CAST}</p>
      ) : null}
    </div>
  );

  if (variant !== 'chip') return picker;

  return (
    <div className="whose-head">
      <button
        type="button"
        className="whose-head-chip"
        aria-expanded={sheetOpen}
        aria-haspopup="dialog"
        aria-label={`${WHO_WE_ARE_WITH}: ${currentLabel}`}
        data-testid="whose-head-chip"
        disabled={disabled}
        onClick={() => setSheetOpen(true)}
      >
        <span className="whose-head-chip__primary">{WHO_WE_ARE_WITH}</span>
        <span className="whose-head-chip__secondary">{currentLabel}</span>
      </button>
      <BottomSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={WHOSE_HEAD}
      >
        <div className="whose-head-sheet" data-testid="whose-head-sheet">
          {picker}
        </div>
      </BottomSheet>
    </div>
  );
}

export function WhoseHeadWarn({
  open,
  onCancel,
  onContinue,
  continueLabel,
}) {
  if (!open) return null;
  return (
    <BottomSheet isOpen={open} onClose={onCancel} title={WHOSE_HEAD}>
      <div className="whose-head-warn" data-testid="whose-head-warn">
        <p>{WHOSE_HEAD_WARN}</p>
        <div className="whose-head-warn__actions">
          <button type="button" onClick={onCancel}>{WHOSE_HEAD_GO_BACK}</button>
          <button type="button" className="whose-head-warn__continue" onClick={onContinue}>
            {continueLabel}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
